// JOBE-0084: Agents list page.
// Wireframe alignment: webui-refresh/operator-console-refresh/wireframes.md
// §3 (list).
//
// JOBE-0161.1: AgentDetail extracted to AgentDetail/AgentDetail.jsx.
// AgentsList and AgentsHeader remain here.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import './agents.css';

const POLL_INTERVAL_MS = 5000;

// formatTime converts an RFC3339 string to a locale date+time string.
function formatTime(t) {
  if (!t) return '—';
  try {
    return new Date(t).toLocaleString();
  } catch (_e) {
    return t;
  }
}

// dash renders a muted em-dash for empty / unknown values.
function dash() {
  return <span className="muted">—</span>;
}

// safeStr returns v only when it is a non-empty string; callers use
// `safeStr(v) || dash()` so that object-valued fields don't reach React
// as children (React Error #31).
function safeStr(v) {
  return typeof v === 'string' && v ? v : null;
}

// usePolledJSON polls `path` every POLL_INTERVAL_MS, returning the most
// recent body. Modelled on useSummaryList in OperatorSummaries.jsx but
// allowed to return either array or object payloads.
function usePolledJSON(path) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!path) return undefined;
    const ctrl = new AbortController();
    let cancelled = false;
    let timerId = null;
    const schedule = () => {
      if (cancelled) return;
      timerId = setTimeout(run, POLL_INTERVAL_MS);
    };
    const run = () => {
      if (cancelled) return;
      fetch(path, { credentials: 'same-origin', signal: ctrl.signal })
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json();
        })
        .then((j) => {
          if (cancelled) return;
          setData(j);
          setError(null);
          schedule();
        })
        .catch((err) => {
          if (cancelled || err.name === 'AbortError') return;
          setError(err.message);
          schedule();
        });
    };
    run();
    return () => {
      cancelled = true;
      ctrl.abort();
      clearTimeout(timerId);
    };
  }, [path]);

  return { data, error, loading: data === null };
}

// ---- AgentsList ----

// readyBadgeClass picks a badge variant based on Ready status.
function readyBadgeClass(ready) {
  if (ready === true) return 'badge badge-ok';
  if (ready === false) return 'badge badge-err';
  return 'badge';
}

// phaseBadgeClass mirrors the phase-pill semantics used elsewhere.
function phaseBadgeClass(phase) {
  switch ((phase || '').toLowerCase()) {
    case 'running': return 'badge badge-ok';
    case 'pending': return 'badge badge-warn';
    case 'failed':  return 'badge badge-err';
    case 'retired': return 'badge';
    default:        return 'badge';
  }
}

// AgentsList is the wireframe §3 page: header + filter row + table +
// bulk-action shell. Data source is /api/admin/agents/summary; columns
// the API does not yet expose render as em-dashes.
export function AgentsList() {
  const { data: rows, error, loading } = usePolledJSON('/api/admin/agents/summary');
  const [search, setSearch] = useState('');
  const [dept, setDept] = useState('all');
  const [ready, setReady] = useState('all');
  const [state, setState] = useState('all');
  const [model, setModel] = useState('all');

  const safeRows = Array.isArray(rows) ? rows : [];

  const deptOptions = useMemo(() => {
    const seen = new Set();
    safeRows.forEach((r) => { if (r.dept) seen.add(r.dept); });
    return Array.from(seen).sort();
  }, [safeRows]);

  const modelOptions = useMemo(() => {
    const seen = new Set();
    safeRows.forEach((r) => { if (r.model) seen.add(r.model); });
    return Array.from(seen).sort();
  }, [safeRows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return safeRows.filter((r) => {
      if (q) {
        const hay = `${r.name || ''} ${r.phase || ''} ${r.role || ''} ${r.dept || ''} ${r.model || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (state !== 'all') {
        if ((r.phase || '').toLowerCase() !== state) return false;
      }
      if (dept !== 'all') {
        if ((r.dept || '') !== dept) return false;
      }
      if (ready !== 'all') {
        const want = ready === 'true';
        if (typeof r.ready !== 'boolean' || r.ready !== want) return false;
      }
      if (model !== 'all') {
        if ((r.model || '') !== model) return false;
      }
      return true;
    });
  }, [safeRows, search, state, dept, ready, model]);

  if (error) {
    return (
      <div className="agents-page">
        <AgentsHeader />
        <div className="card"><p className="errorMsg">Failed to load: {error}</p></div>
      </div>
    );
  }
  if (loading) {
    return (
      <div className="agents-page">
        <AgentsHeader />
        <div className="card"><p>Loading…</p></div>
      </div>
    );
  }

  const isEmpty = safeRows.length === 0;

  return (
    <div className="agents-page">
      <AgentsHeader />
      <div className="card agents-filters" role="search" aria-label="Agent filters">
        <input
          className="agents-search"
          type="search"
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search agents"
        />
        <label className="agents-filter">
          Dept
          <select value={dept} onChange={(e) => setDept(e.target.value)} aria-label="Filter by department">
            <option value="all">All</option>
            {deptOptions.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </label>
        <label className="agents-filter">
          Ready
          <select value={ready} onChange={(e) => setReady(e.target.value)} aria-label="Filter by readiness">
            <option value="all">All</option>
            <option value="true">Ready</option>
            <option value="false">Not ready</option>
          </select>
        </label>
        <label className="agents-filter">
          State
          <select value={state} onChange={(e) => setState(e.target.value)} aria-label="Filter by state">
            <option value="all">All</option>
            <option value="running">Running</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="retired">Retired</option>
          </select>
        </label>
        <label className="agents-filter">
          Model
          <select value={model} onChange={(e) => setModel(e.target.value)} aria-label="Filter by model">
            <option value="all">All</option>
            {modelOptions.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
      </div>

      {isEmpty ? (
        <div className="card dispatcher-empty">
          <p>No agents yet.</p>
          <p className="dispatcher-empty-hint">
            Agents will appear here once they're registered with the operator.
          </p>
        </div>
      ) : (
        <div className="card">
          <div className="agents-result-count" aria-live="polite">
            {filtered.length === safeRows.length
              ? `${safeRows.length} agent${safeRows.length === 1 ? '' : 's'}`
              : `${filtered.length} of ${safeRows.length}`}
          </div>
          <table className="dispatcher-table agents-table">
            <thead>
              <tr>
                <th className="agents-col-name">Name</th>
                <th>Role</th>
                <th>Dept</th>
                <th>Ready</th>
                <th>Phase</th>
                <th>Model</th>
                <th className="agents-col-num">Work</th>
                <th className="agents-col-num">$</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr className="agents-empty-row">
                  <td colSpan={8}>No agents match the current filters.</td>
                </tr>
              ) : filtered.map((row) => (
                <tr key={row.name}>
                  <td className="agents-col-name">
                    {/* JOBE-0161.5: canonical deep-link route (ADR-0033 §D9) */}
                    <Link to={`/agent/${encodeURIComponent(row.name)}`}>
                      {row.name}
                    </Link>
                  </td>
                  <td>{row.role || dash()}</td>
                  <td>{row.dept || dash()}</td>
                  <td>
                    {typeof row.ready === 'boolean'
                      ? <span className={readyBadgeClass(row.ready)}>{row.ready ? 'Yes' : 'No'}</span>
                      : dash()}
                  </td>
                  <td>
                    {row.phase
                      ? <span className={phaseBadgeClass(row.phase)}>{row.phase}</span>
                      : dash()}
                  </td>
                  <td>{row.model || dash()}</td>
                  <td className="agents-col-num">{dash()}</td>
                  <td className="agents-col-num">{dash()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="agents-bulk" aria-label="Bulk actions">
            <span>Bulk actions:</span>
            <button type="button" disabled title="Coming soon">Restart</button>
            <button type="button" disabled title="Coming soon">Pause</button>
            <button type="button" disabled title="Coming soon">Export</button>
          </div>
        </div>
      )}
    </div>
  );
}

// AgentsHeader is the page header strip prescribed by wireframe §3: title
// on the left, primary "+ New Agent" action on the right. The button is
// disabled today; creating an agent is out of scope (see JOBE-0084).
function AgentsHeader() {
  return (
    <div className="agents-header">
      <div>
        <h2>Agents</h2>
        <span className="agents-sub">Operator console</span>
      </div>
      <div className="agents-actions">
        <button type="button" className="agents-action" disabled title="Coming soon">+ New Agent</button>
      </div>
    </div>
  );
}

// AgentDetail tests have moved to AgentDetail/AgentDetail.test.jsx (JOBE-0161.1).

// formatTime is exposed for tests that want to assert timestamp rendering.
export { formatTime };
